import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoneWorkReport } from '@cloudsign/shared';
import { api } from '../../../lib/api';
import { formatDateTime } from '../../../lib/format';
import { t } from '../../../lib/i18n';
import { EmptyRow, FilterBar, LinkButton, PageHeader, Pager, SelectField, Spinner, TableWrap, Th } from '../../../components/ui';
import { ErrorPanel } from '../../../components/ErrorPanel';
import { ExportButton } from '../../../components/ExportButton';
import { DateRangePicker, isRangeValid, presetRanges } from '../../../components/phase2';
import type { DateRange } from '../../../components/phase2';
import { useListState } from '../../../lib/useList';
import { usePermitSites } from '../api';
import { loneKey } from './api';

const PAGE_SIZE = 50;

/** "4 min" / "1 h 5 min" for the slowest response. */
export function responseText(seconds: number | null): string {
  if (seconds === null) return '-';
  const m = Math.round(seconds / 60);
  if (m < 1) return t('p3lw.report.underMinute');
  return m < 60 ? t('p3lw.report.minutes', { n: m }) : t('p3lw.report.hours', { h: Math.floor(m / 60), n: m % 60 });
}

/** Lone-working report: sessions with check-ins, misses, escalations, panics and response times, with export. */
export function LoneReportPage() {
  const list = useListState();
  const [range, setRange] = useState<DateRange>(() => presetRanges()[2]!.range);
  const [siteId, setSiteId] = useState('');
  const sites = usePermitSites();
  const ready = range.from !== '' && range.to !== '' && isRangeValid(range);
  const q = useQuery({
    queryKey: [...loneKey, 'report', range.from, range.to, siteId, list.page],
    enabled: ready,
    queryFn: () => api.get('/lone-working/report', { query: { from: range.from, to: range.to, siteId, page: list.page, pageSize: PAGE_SIZE }, schema: LoneWorkReport }),
  });
  return (
    <div className="space-y-4">
      <PageHeader
        title={t('p3lw.report.title')}
        description={t('p3lw.report.description')}
        actions={
          <>
            <LinkButton to="/lone-working">{t('p3lw.report.back')}</LinkButton>
            <ExportButton path="/lone-working/report/export" query={{ from: range.from, to: range.to, siteId }} fileName="lone-working" disabled={!ready} />
          </>
        }
      />
      <FilterBar columns="sm:grid-cols-2">
        <DateRangePicker
          label={t('p3lw.report.range')}
          value={range}
          onChange={(r) => {
            setRange(r);
            list.resetPage();
          }}
        />
        <SelectField
          label={t('p3pt.field.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('p3pt.filter.anySite')}</option>
          {(sites.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      {!ready ? null : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <>
          <TableWrap caption={t('p3lw.report.title')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('p3lw.sessions.person')}</Th>
                <Th>{t('p3lw.sessions.site')}</Th>
                <Th>{t('p3lw.report.started')}</Th>
                <Th>{t('p3lw.report.ended')}</Th>
                <Th>{t('p3lw.report.checkIns')}</Th>
                <Th>{t('p3lw.report.missed')}</Th>
                <Th>{t('p3lw.report.escalations')}</Th>
                <Th>{t('p3lw.report.panics')}</Th>
                <Th>{t('p3lw.report.slowest')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? (
                <EmptyRow cols={9} />
              ) : (
                q.data.items.map((r) => (
                  <tr key={r.sessionId}>
                    <td className="px-4 py-2.5 font-medium">{r.memberName}</td>
                    <td className="px-4 py-2.5">{r.siteName}</td>
                    <td className="px-4 py-2.5">{formatDateTime(r.startedAt)}</td>
                    <td className="px-4 py-2.5">{r.endedAt ? formatDateTime(r.endedAt) : t('p3lw.report.stillOn')}</td>
                    <td className="px-4 py-2.5">{r.checkIns}</td>
                    <td className="px-4 py-2.5">{r.missed}</td>
                    <td className="px-4 py-2.5">{r.escalations}</td>
                    <td className="px-4 py-2.5">{r.panics}</td>
                    <td className="px-4 py-2.5">{responseText(r.slowestResponseSeconds)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </div>
  );
}
